import {
  issueFromFields,
  parseNestedBlockerItems,
  PAYROLL_RUN_READINESS_ISSUE_CODES as ISSUE,
  type PayrollReadinessIssue,
  type PayrollReadinessRawSeverity,
} from './payroll/payroll-readiness-issue';
import {
  DEFAULT_PAY_CONTEXT_ENFORCEMENT_MODE,
  ISSUE_PAY_HOLD_OWNER_PARAMETER,
  issueResolutionOwner,
  rowApprovalBlockingIssues,
  rowCoordinatorApprovalBlockingIssues,
  type PayContextEnforcementMode,
} from './payroll/blocker-impact';
import { describeBlocker } from './payroll/blocker-descriptor';
import {
  isV13BreakSemantics,
  resolveBreakTreatment,
  type BreakSemantics,
  type BreakTreatmentRuleBlock,
} from './payroll/break-treatment';

export type { PayrollReadinessIssue } from './payroll/payroll-readiness-issue';

export interface PayrollRunReadinessRowLike {
  id: string;
  /** Optional billed client identity used to build exact care/agreement remedies. */
  clientId?: string | null;
  staffName?: string | null;
  /** Optional; when present, copied onto structured issues for P2 routing. */
  staffId?: string | null;
  employmentType?: string | null;
  schadsLevel?: string | null;
  classificationLabel?: string | null;
  awardCode?: string | null;
  paymentMethod?: string | null;
  isSalaryFlat?: boolean | null;
  payContextSnapshot?: Record<string, unknown> | null;
  needsAwardReview?: boolean | null;
  totalHours?: number | string | null;
  totalPay?: number | string | null;
  allowances?: number | string | null;
  status?: string | null;
  isOvertime?: boolean | null;
  isPublicHoliday?: boolean | null;
  openDisputeCount?: number | null;
  ordinaryHoursLimitPerShift?: number | null;
  fullTimeOrdinaryHoursPerDay?: number | null;
  sourceEvidence?: unknown;
  sourceEvidenceRequired?: boolean | null;
  /**
   * Worked-window bounds (ISO instants). Optional; when present on two or
   * more rows for the SAME staffId, cross-row overlap detection fires the
   * possible-duplicate blocker on every member of an overlapping pair.
   */
  actualStart?: string | null;
  actualEnd?: string | null;
  payContextStatus?: string | null;
  payContextBlockers?: unknown;
  breakTreatmentStatus?: string | null;
  /**
   * `tenant_configs.payroll_second_person_review_enabled`. Undefined means the
   * policy was not loaded, which is treated as ENFORCED — the gate must never
   * open by omission.
   */
  payrollSecondPersonReviewEnabled?: boolean | null;
  breakTreatmentBlockers?: unknown;
  breakTreatmentSnapshot?: Record<string, unknown> | null;
  /** Verified against all tenant break blocks, including inactive versions. */
  noTenantBreakRule?: boolean;
  /**
   * Break rules this row is bound by (release ruling 28 Oct 2026). Omitted =
   * LEGACY_28D1: exactly production main 28d166931e. V13 only for a tenant whose
   * payroll rebuild gate is ON; there BREAK_DURATION_UNRESOLVED is a payroll pay
   * hold, never a coordinator approval gate.
   */
  breakSemantics?: BreakSemantics;
  recordedBreakMinutes?: number | null;
  /** Current-version/source/category proof, supplied by an authoritative caller. */
  currentBreakRuleProof?: {
    payRuleVersionId: string;
    employeeCategory: string | null;
    ruleBlocks: BreakTreatmentRuleBlock[];
    approvedTakenFactAbsent: boolean;
  } | null;
  payEventFactSetStatus?: string | null;
  payEventFactSetBlockers?: unknown;
  /** Durable provider-manual allowance evidence exists but is not priced yet. */
  providerManualAllowancePricingPending?: boolean | null;
  /** Set when clock-honesty already failed on the capture path. */
  clockHonestyFailed?: boolean | null;
  /** Set when the billed client has no current service agreement. */
  serviceAgreementGap?: boolean | null;
  /** Set when the billed visit has no acknowledged care-plan inclusion. */
  carePlanGap?: boolean | null;
}

export const NOT_APPROVED_EXPORT_BLOCKER = 'Timesheet is not yet approved for payroll export.';
export const MISSING_COMPLETED_TIME_SOURCE_EVIDENCE_BLOCKER =
  'Completed-time source evidence is missing or incomplete.';
export const IMPLAUSIBLE_WORKED_DURATION_BLOCKER =
  'Worked duration is under five minutes — correct the actual times (or void the row) before approval.';
export const AUTO_CLOSED_TIMESHEET_BLOCKER =
  'Auto-closed after a missed clock-out — confirm the actual end time before approval.';
export const AUTO_CLOSED_CAPTURE_METHOD = 'AUTO_CLOSED_MISSED_CLOCK_OUT';
export const POSSIBLE_DUPLICATE_TIMESHEET_BLOCKER =
  'Another timesheet overlaps this worked window — resolve the pair before approval.';
export const CLOCK_HONESTY_BLOCKER = 'Clock-honesty evidence failed — resolve before approval.';
export const SERVICE_AGREEMENT_GAP_BLOCKER =
  'Service agreement is missing or stale — resolve before approval.';
export const CARE_PLAN_GAP_BLOCKER =
  'Care-plan acknowledgement is missing — resolve before approval.';

/**
 * Two sane-looking rows can still pay the same minutes twice: Golden Bloom
 * 15-Aug-2026 found one worker double-clocking couple-household visits
 * (identical 06:25–08:24 pairs, ~$270 across one week) with NOTHING firing —
 * the possible-duplicate blocker only listened to the AUTO_CLOSED sweep's
 * `possibleDuplicate` evidence flag. This tolerance mirrors the DB approval
 * trigger: boundary touches and tap-bleed up to five minutes are normal
 * back-to-back clocking (live example: consecutive visits sharing 17:00 to
 * 17:01); anything past it is the same minutes claimed twice. The
 * create/correction guards stay STRICT zero-tolerance — this looser bound
 * exists only for clock-path rows those guards never saw.
 */
export const WORKED_TIME_OVERLAP_TOLERANCE_MINUTES = 5;

/**
 * Cross-row worked-window overlap detection. Returns the ids of every row
 * that overlaps another non-REJECTED row for the same staff member by more
 * than the tolerance. Rows without staffId or parseable bounds are skipped
 * (fail-open per row, because most callers cannot supply actuals — the DB
 * approval trigger is the enforcement backstop).
 */
export function computeOverlappingWorkedTimeRowIds(
  rows: PayrollRunReadinessRowLike[],
): Set<string> {
  const byStaff = new Map<string, Array<{ id: string; start: number; end: number }>>();
  for (const row of rows) {
    if (String(row.status || '').toUpperCase() === 'REJECTED') continue;
    const staffId = row.staffId ?? null;
    if (!staffId) continue;
    const start = Date.parse(String(row.actualStart ?? ''));
    const end = Date.parse(String(row.actualEnd ?? ''));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    const list = byStaff.get(staffId) ?? [];
    list.push({ id: row.id, start, end });
    byStaff.set(staffId, list);
  }
  const overlapping = new Set<string>();
  const toleranceMs = WORKED_TIME_OVERLAP_TOLERANCE_MINUTES * 60_000;
  for (const list of byStaff.values()) {
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const a = list[i]!;
        const b = list[j]!;
        const overlapMs = Math.min(a.end, b.end) - Math.max(a.start, b.start);
        if (overlapMs > toleranceMs) {
          overlapping.add(a.id);
          overlapping.add(b.id);
        }
      }
    }
  }
  return overlapping;
}

export interface PayrollRunReviewRow {
  id: string;
  staffName: string;
  status: string;
  blockerCount: number;
  /** Legacy English strings — behaviour preserved byte-for-byte (incl. 3-item nested cap). */
  blockers: string[];
  /**
   * Reasons `canExport` is false that are deliberately NOT approval blockers.
   *
   * Currently only unpriced provider manual allowance evidence — payroll has to
   * supply the rate. It does NOT carry pay-fact sets awaiting a second
   * reviewer: that control was removed from the payroll gates entirely on
   * 2026-09-07 (owner decision) and is a `manualReviewFlags` entry instead.
   *
   * Without surfacing these a row reads "Review required" with no stated
   * reason, which is the silent friction that splitting them out created.
   *
   * OPTIONAL on the interface: `summarisePayrollRunReadiness` always sets it,
   * but making it required was a breaking change for every other constructor
   * of this type (it broke seven test fixtures). Read it with `?.length`.
   */
  exportOnlyBlockers?: string[];
  /**
   * Structured issues — full un-truncated set (P1-A additive).
   * Downstream gate rewiring is P1-B.
   */
  issues: PayrollReadinessIssue[];
  manualReviewFlags: string[];
  canExport: boolean;
  /**
   * The row's only obstacle is that its pay context has not resolved yet and it
   * is not approved. Waiting, not broken — do not present it as blocked to a
   * coordinator.
   */
  /** Optional so existing callers need no change; absent reads as "blocked",
   *  which is the conservative default. */
  awaitingResolutionOnly?: boolean;
}

export interface PayrollRunReadinessSummary {
  totalRows: number;
  exportableRowCount: number;
  /** Rows with any blocker, including "not approved yet". Export semantics. */
  blockedRowCount: number;
  /** Rows blocked for a reason a coordinator can act on. Use this in coordinator UI. */
  actionableBlockedRowCount: number;
  /** Rows whose only blocker is unresolved pay context on an unapproved row. */
  awaitingResolutionRowCount: number;
  /**
   * Rows with provider-manual allowance pricing still pending (visibility-only).
   * Pay-run binding is not refused for pending-pricing intents; the shift may
   * still snapshot while the allowance stays unpaid — surface as "held for money".
   */
  financialHoldRowCount: number;
  pendingApprovalCount: number;
  rejectedCount: number;
  openDisputeCount: number;
  missingClassificationCount: number;
  missingPayCount: number;
  allowanceReviewCount: number;
  overtimeReviewCount: number;
  publicHolidayReviewCount: number;
  fullTimeAgreementGapCount: number;
  rows: PayrollRunReviewRow[];
}

function asNumber(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getOrdinaryHoursLimit(row: PayrollRunReadinessRowLike) {
  return Number(row.ordinaryHoursLimitPerShift ?? row.fullTimeOrdinaryHoursPerDay ?? 10);
}

function nonEmptyText(value: unknown): boolean {
  return typeof value === 'string'
    ? value.trim().length > 0
    : value !== null && value !== undefined;
}

const COORDINATOR_CAPTURE_METHODS = new Set([
  'COORDINATOR',
  'COORDINATOR_ON_BEHALF',
  'COORDINATOR_ON_BEHALF_CLOCK_OUT',
]);

export function isCoordinatorTimeCaptureMethod(value: unknown): boolean {
  const normalised = String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  return COORDINATOR_CAPTURE_METHODS.has(normalised);
}

/**
 * Import / approve / export gate. A bare coordinator token is not enough —
 * interactive coordinators stamp supervisor or signedOffAt on save so this
 * still passes for forgotten-clock corrections they actually submitted.
 */
export function hasCompletedTimeSourceEvidence(sourceEvidence: unknown): boolean {
  if (!sourceEvidence || typeof sourceEvidence !== 'object' || Array.isArray(sourceEvidence)) {
    return false;
  }
  const evidence = sourceEvidence as Record<string, unknown>;
  return Boolean(
    nonEmptyText(evidence.captureMethod) &&
    (nonEmptyText(evidence.supervisor) ||
      nonEmptyText(evidence.sourceReference) ||
      nonEmptyText(evidence.signedOffAt)),
  );
}

function normaliseStatus(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

/** Legacy string extraction for blockers[] — must stay identical to pre-P1-A behaviour. */
/**
 * Pay-context codes that must not surface as coordinator blockers.
 *
 * PAY_FACT_SET_NOT_APPROVED is the second-person review on captured pay
 * evidence. Owner decision 2026-09-07 (Paul Benson): Medea's team is too small
 * to staff a second reviewer, so it is removed from the payroll gates entirely
 * and kept as an advisory flag. Self-approval guards, care-plan signing,
 * worker-screening two-person verification and the Services Australia controls
 * are deliberately untouched.
 */
const SECOND_PERSON_REVIEW_PAY_CONTEXT_CODES = new Set<string>(['PAY_FACT_SET_NOT_APPROVED']);

/**
 * Is the payroll second-person review enforced for this row's tenant?
 *
 * Defaults to TRUE — an unknown or unloaded policy must keep the control on,
 * never silently open the gate. Only an explicit `false` from
 * `tenant_configs.payroll_second_person_review_enabled` disables it.
 */
function payrollSecondPersonReviewEnforced(row: PayrollRunReadinessRowLike): boolean {
  return row.payrollSecondPersonReviewEnabled !== false;
}

function nestedCodeOf(item: unknown): string {
  if (item && typeof item === 'object') {
    const code = (item as Record<string, unknown>).code;
    if (typeof code === 'string') return code.trim().toUpperCase();
  }
  return '';
}

/** All entries, unchanged — used when the second-person review is enforced. */
function jsonBlockerEntries(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** Strip second-person-review entries before they become blocker strings. */
function payrollBlockingPayContextEntries(value: unknown): unknown[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item) => !SECOND_PERSON_REVIEW_PAY_CONTEXT_CODES.has(nestedCodeOf(item)));
}

function advisoryPayContextMessages(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return jsonBlockers(
    value.filter((item) => SECOND_PERSON_REVIEW_PAY_CONTEXT_CODES.has(nestedCodeOf(item))),
  );
}

function jsonBlockers(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const message = record.message ?? record.reason ?? record.code ?? record.severity;
        return typeof message === 'string' ? message.trim() : JSON.stringify(item);
      }
      return String(item ?? '').trim();
    })
    .filter(Boolean);
}

function statusNeedsResolution(
  status: string | null | undefined,
  resolvedStatuses: ReadonlySet<string>,
): boolean {
  const normalized = normaliseStatus(status);
  return normalized.length > 0 && !resolvedStatuses.has(normalized);
}

function providedField<K extends keyof PayrollRunReadinessRowLike>(
  row: PayrollRunReadinessRowLike,
  key: K,
): boolean {
  return Object.prototype.hasOwnProperty.call(row, key);
}

function providedStatusNeedsResolution(
  row: PayrollRunReadinessRowLike,
  key: 'payContextStatus' | 'breakTreatmentStatus' | 'payEventFactSetStatus',
  resolvedStatuses: ReadonlySet<string>,
): boolean {
  if (!providedField(row, key)) return false;
  return !resolvedStatuses.has(normaliseStatus(row[key]));
}

function payContextText(
  snapshot: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = snapshot?.[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

function upperStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .map((item) => (typeof item === 'string' ? item.trim().toUpperCase() : ''))
        .filter(Boolean),
    ),
  ).sort();
}

function breakTreatmentFactParameters(row: PayrollRunReadinessRowLike): Record<string, unknown> {
  const snapshot = row.breakTreatmentSnapshot;
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return {};
  const requiredFactCodes = upperStringArray(
    snapshot.requiredFactCodes ?? snapshot.required_fact_codes,
  );
  const missingFactCodes = upperStringArray(
    snapshot.missingFactCodes ?? snapshot.missing_fact_codes,
  );
  const positiveFactCodes = upperStringArray(
    snapshot.unsatisfiedPositiveFactCodes ?? snapshot.unsatisfied_positive_fact_codes,
  );
  return {
    ...(requiredFactCodes.length > 0 ? { requiredFactCodes } : {}),
    ...(missingFactCodes.length > 0 ? { missingFactCodes } : {}),
    ...(positiveFactCodes.length > 0 ? { positiveFactCodes } : {}),
  };
}

function isSalaryFlatRow(row: PayrollRunReadinessRowLike): boolean {
  if (typeof row.isSalaryFlat === 'boolean') return row.isSalaryFlat;
  if (typeof row.paymentMethod === 'string' && row.paymentMethod.trim()) {
    return row.paymentMethod.trim().toUpperCase() === 'SALARY_FLAT';
  }
  const snapshot = row.payContextSnapshot;
  return (
    payContextText(snapshot, 'baseAwardCode', 'base_award_code') === 'SALARY_FLAT' ||
    payContextText(snapshot, 'classificationStream', 'classification_stream') === 'SALARY_FLAT' ||
    payContextText(snapshot, 'paidClassificationStream', 'paid_classification_stream') ===
      'SALARY_FLAT'
  );
}

/**
 * Human-readable labels for issues that block payroll approval under the
 * tenant enforcement mode. Decision comes from `blockerImpact(...).blocksApproval`
 * via `rowApprovalBlockingIssues` — not from filtering legacy `blockers[]`.
 *
 * When `mode` is omitted, defaults to ENFORCE_CONTEXT (safe unresolved default).
 */
export function approvalBlockersForPayrollApproval(
  readiness:
    | Pick<PayrollRunReviewRow, 'issues'>
    | Pick<PayrollRunReviewRow, 'issues' | 'blockers'>
    | null
    | undefined,
  mode: PayContextEnforcementMode = DEFAULT_PAY_CONTEXT_ENFORCEMENT_MODE,
): string[] {
  return rowApprovalBlockingIssues(readiness, mode).map(humanLabelForReadinessIssue);
}

/**
 * Human-readable labels for issues that block COORDINATOR approval under the
 * tenant enforcement mode (separation-of-duties gate, 2026-08-19).
 *
 * In every tenant mode, this contains worked-time-integrity labels only.
 * Payroll-owned pay codes are excluded here and surfaced via
 * `outstandingPayResolutionCodes`. The pay-run snapshot / export gates are
 * unchanged and still refuse them.
 */
export function coordinatorApprovalBlockersForPayrollApproval(
  readiness:
    | Pick<PayrollRunReviewRow, 'issues'>
    | Pick<PayrollRunReviewRow, 'issues' | 'blockers'>
    | null
    | undefined,
  mode: PayContextEnforcementMode = DEFAULT_PAY_CONTEXT_ENFORCEMENT_MODE,
): string[] {
  return rowCoordinatorApprovalBlockingIssues(readiness, mode).map(humanLabelForReadinessIssue);
}

/** Plain-English label for toasts / server error strings (BlockerDescriptor registry). */
export function humanLabelForReadinessIssue(issue: PayrollReadinessIssue): string {
  const descriptor = describeBlocker(issue.code);
  const paramMessage =
    typeof issue.parameters?.message === 'string' ? issue.parameters.message.trim() : '';
  // Nested free-form payloads often only carry a useful message in parameters.
  if (
    paramMessage &&
    (issue.code === ISSUE.PAY_CONTEXT_NESTED ||
      issue.code === ISSUE.BREAK_NESTED ||
      issue.code === ISSUE.PAY_FACT_NESTED ||
      descriptor.title === 'Payroll readiness issue')
  ) {
    return paramMessage;
  }
  return descriptor.detail || descriptor.title;
}

/**
 * Pre-P1-B approval gate (string filter): all blockers except NOT_APPROVED.
 * Retained for the no-control-dropped regression test only — do not use in product paths.
 */
export function legacyApprovalBlockersFromStrings(
  readiness: Pick<PayrollRunReviewRow, 'blockers'> | null | undefined,
): string[] {
  return (readiness?.blockers ?? []).filter((blocker) => blocker !== NOT_APPROVED_EXPORT_BLOCKER);
}

function pushIssue(
  issues: PayrollReadinessIssue[],
  partial: {
    domain: PayrollReadinessIssue['domain'];
    code: string;
    rawSeverity: PayrollReadinessRawSeverity;
    resourceId: string;
    staffId?: string | null;
    parameters?: Record<string, unknown>;
  },
): void {
  issues.push(
    issueFromFields({
      domain: partial.domain,
      code: partial.code,
      rawSeverity: partial.rawSeverity,
      resourceType: 'timesheet',
      resourceId: partial.resourceId,
      staffId: partial.staffId,
      parameters: partial.parameters,
    }),
  );
}

/**
 * Nested pay-context severity: use resolver severity when present; SOFT for fact-set /
 * identity-drift families; HARD otherwise.
 */
function nestedPayContextSeverity(
  code: string | null,
  severity: PayrollReadinessRawSeverity | null,
): PayrollReadinessRawSeverity {
  if (severity === 'HARD' || severity === 'SOFT') return severity;
  if (!code) return 'HARD';
  if (
    code === 'PAY_FACT_SET_REQUIRED' ||
    code === 'PAY_FACT_SET_NOT_APPROVED' ||
    code === 'PAY_FACTS_MISSING' ||
    code.endsWith('_IDENTITY_DRIFT')
  ) {
    return 'SOFT';
  }
  return 'HARD';
}

function evidenceRecord(sourceEvidence: unknown): Record<string, unknown> | null {
  if (!sourceEvidence || typeof sourceEvidence !== 'object' || Array.isArray(sourceEvidence)) {
    return null;
  }
  return sourceEvidence as Record<string, unknown>;
}

function evidenceOrRowFlag(
  row: PayrollRunReadinessRowLike,
  rowFlag: boolean | null | undefined,
  evidenceKeys: readonly string[],
): boolean {
  if (rowFlag === true) return true;
  const evidence = evidenceRecord(row.sourceEvidence);
  if (!evidence) return false;
  return evidenceKeys.some((key) => evidence[key] === true);
}

function breakStatusIssueCode(status: string): {
  code: string;
  rawSeverity: PayrollReadinessRawSeverity;
} {
  if (status === 'BLOCKED') {
    return { code: ISSUE.BREAK_TREATMENT_BLOCKED, rawSeverity: 'HARD' };
  }
  if (!status) {
    return { code: ISSUE.BREAK_STATUS_UNKNOWN, rawSeverity: 'SOFT' };
  }
  return { code: ISSUE.BREAK_TREATMENT_NOT_RESOLVED, rawSeverity: 'HARD' };
}

export function summarisePayrollRunReadiness(
  rows: PayrollRunReadinessRowLike[],
): PayrollRunReadinessSummary {
  const overlappingRowIds = computeOverlappingWorkedTimeRowIds(rows);
  const summaries = rows.map<PayrollRunReviewRow>((row) => {
    const blockers: string[] = [];
    /**
     * Export-only gates. Pricing is payroll's to finalise and must never reach
     * back into the timesheet or rostering stage, so these stay OUT of
     * `blockers` (which drives blockerCount, the red "blocked" row and the
     * coordinator exception queue) while still refusing pay-run export.
     */
    const exportOnlyBlockers: string[] = [];
    const issues: PayrollReadinessIssue[] = [];
    const manualReviewFlags: string[] = [];
    const status = String(row.status || 'PENDING').toUpperCase();
    const employmentType = String(row.employmentType || '').toUpperCase();
    const awardCode = String(row.awardCode || '').toUpperCase();
    const classificationLabel = String(row.classificationLabel || row.schadsLevel || '').trim();
    const salaryFlat = isSalaryFlatRow(row);
    const totalHours = asNumber(row.totalHours) ?? 0;
    const totalPay = asNumber(row.totalPay) ?? 0;
    const allowances = asNumber(row.allowances) ?? 0;
    const openDisputeCount = Number(row.openDisputeCount || 0);
    const ordinaryHoursLimit = getOrdinaryHoursLimit(row);
    const resourceId = row.id;
    const staffId = row.staffId ?? null;
    let evidenceFlaggedDuplicate = false;

    if (status !== 'APPROVED' && status !== 'PAID') {
      blockers.push(NOT_APPROVED_EXPORT_BLOCKER);
      pushIssue(issues, {
        domain: 'APPROVAL',
        code: ISSUE.NOT_APPROVED,
        rawSeverity: 'SOFT',
        resourceId,
        staffId,
      });
    }
    // Golden Bloom 14-Aug-2026: eight sub-5-minute glitch clock pairs sat
    // RESOLVED and unblocked, three carrying corrupted penalty multipliers.
    // A worked duration under five minutes is never approvable as-is — it is
    // corrected (real visit, mis-clocked) or voided (accidental tap).
    if (totalHours < 5 / 60) {
      blockers.push(IMPLAUSIBLE_WORKED_DURATION_BLOCKER);
      pushIssue(issues, {
        domain: 'EVIDENCE',
        code: ISSUE.IMPLAUSIBLE_WORKED_DURATION,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
      });
    }
    if (row.sourceEvidenceRequired && !hasCompletedTimeSourceEvidence(row.sourceEvidence)) {
      blockers.push(MISSING_COMPLETED_TIME_SOURCE_EVIDENCE_BLOCKER);
      pushIssue(issues, {
        domain: 'EVIDENCE',
        code: ISSUE.MISSING_COMPLETED_TIME_SOURCE_EVIDENCE,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
      });
    }
    // Daily Time Review v1: the attendance sweep closes missed-clock-out shifts
    // at the SCHEDULED end and stamps this capture method. The end time is
    // system-fabricated, so approval is blocked until a human confirms or
    // corrects the actuals (the correction flow replaces the capture method).
    // This is deliberately independent of the app-clock evidence gate: on
    // multi-assignment shifts the shift-level GPS evidence of ANOTHER worker
    // would otherwise make this row look clock-verified.
    if (
      row.sourceEvidence &&
      typeof row.sourceEvidence === 'object' &&
      !Array.isArray(row.sourceEvidence)
    ) {
      const capturedEvidence = row.sourceEvidence as Record<string, unknown>;
      if (capturedEvidence.captureMethod === AUTO_CLOSED_CAPTURE_METHOD) {
        blockers.push(AUTO_CLOSED_TIMESHEET_BLOCKER);
        // Grok audit 2026-07-14 (P0): the legacy string alone no longer gates —
        // approval decides from blockerImpact over typed issues.
        pushIssue(issues, {
          domain: 'EVIDENCE',
          code: ISSUE.AUTO_CLOSED_PENDING_CONFIRMATION,
          rawSeverity: 'HARD',
          resourceId,
          staffId,
        });
      }
      if (capturedEvidence.possibleDuplicate === true) {
        evidenceFlaggedDuplicate = true;
      }
    }
    // One push site for both duplicate signals: the sweep's evidence flag
    // (sibling may be outside the batch) and cross-row overlap detection
    // (GB 15-Aug double-clocked couple visits — two sane rows, no flag).
    if (evidenceFlaggedDuplicate || overlappingRowIds.has(resourceId)) {
      blockers.push(POSSIBLE_DUPLICATE_TIMESHEET_BLOCKER);
      pushIssue(issues, {
        domain: 'EVIDENCE',
        code: ISSUE.POSSIBLE_DUPLICATE_TIMESHEET,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
      });
    }
    if (
      evidenceOrRowFlag(row, row.clockHonestyFailed, ['clockHonestyFailed', 'clock_honesty_failed'])
    ) {
      blockers.push(CLOCK_HONESTY_BLOCKER);
      pushIssue(issues, {
        domain: 'EVIDENCE',
        code: ISSUE.CLOCK_HONESTY,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
      });
    }
    if (
      evidenceOrRowFlag(row, row.serviceAgreementGap, [
        'serviceAgreementGap',
        'service_agreement_gap',
      ])
    ) {
      blockers.push(SERVICE_AGREEMENT_GAP_BLOCKER);
      pushIssue(issues, {
        domain: 'EVIDENCE',
        code: ISSUE.SERVICE_AGREEMENT_GAP,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: row.clientId ? { clientId: row.clientId } : undefined,
      });
    }
    if (evidenceOrRowFlag(row, row.carePlanGap, ['carePlanGap', 'care_plan_gap'])) {
      blockers.push(CARE_PLAN_GAP_BLOCKER);
      pushIssue(issues, {
        domain: 'EVIDENCE',
        code: ISSUE.CARE_PLAN_GAP,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: row.clientId ? { clientId: row.clientId } : undefined,
      });
    }
    if (!salaryFlat && !classificationLabel) {
      blockers.push('Award classification is missing.');
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code: ISSUE.AWARD_CLASSIFICATION_MISSING,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
      });
    }
    // Phase 5 §7: SCHADS, Nurses, Aged Care, HPSS all supported.
    const SUPPORTED_AWARD_CODES = new Set(['MA000100', 'MA000034', 'MA000018', 'MA000027']);
    if (awardCode && !SUPPORTED_AWARD_CODES.has(awardCode)) {
      blockers.push(
        'Automatic payroll export is limited to SCHADS (MA000100), Nurses (MA000034), Aged Care (MA000018), and HPSS (MA000027) classified staff. Review this timesheet under the recorded award before export.',
      );
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code: ISSUE.UNSUPPORTED_AWARD,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: { awardCode },
      });
    }
    if (!salaryFlat && row.needsAwardReview) {
      blockers.push('Award classification requires payroll review before export.');
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code: ISSUE.AWARD_REVIEW_REQUIRED,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
      });
    }
    if (!salaryFlat && totalHours > 0 && totalPay <= 0) {
      blockers.push('Pay calculation is missing or zero.');
      pushIssue(issues, {
        domain: 'CALCULATION',
        code: ISSUE.ZERO_PAY,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: { totalHours, totalPay },
      });
    }
    if (openDisputeCount > 0) {
      blockers.push(
        `${openDisputeCount} open payroll dispute${openDisputeCount === 1 ? '' : 's'} remain.`,
      );
      pushIssue(issues, {
        domain: 'APPROVAL',
        code: ISSUE.OPEN_PAYROLL_DISPUTE,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: { openDisputeCount },
      });
    }
    if (employmentType === 'FULL_TIME' && totalHours > 8 && ordinaryHoursLimit < 10) {
      blockers.push('Shift exceeds 8 ordinary hours without a recorded 10-hour agreement.');
      pushIssue(issues, {
        domain: 'APPROVAL',
        code: ISSUE.FT_OVER_8H_WITHOUT_10H_AGREEMENT,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: { totalHours, ordinaryHoursLimit },
      });
    }

    // ── Pay context: blockers keep 3-item cap; issues emit FULL nested set ──
    // Tenant policy decides whether the payroll second-person review is a gate
    // or a note. Default (and any unresolved policy) keeps it a gate.
    const secondPersonReviewEnforced = payrollSecondPersonReviewEnforced(row);
    const payContextEntries = secondPersonReviewEnforced
      ? jsonBlockerEntries(row.payContextBlockers)
      : payrollBlockingPayContextEntries(row.payContextBlockers);
    const payrollBlockingPayContext = payContextEntries.filter(
      (item) => nestedCodeOf(item) !== 'PAY_ROLE_CARE_MINUTES_CATEGORY_MISSING',
    );
    const payContextBlockers = jsonBlockers(payrollBlockingPayContext);
    const payContextNested = parseNestedBlockerItems(payContextEntries);
    manualReviewFlags.push(
      ...jsonBlockers(
        jsonBlockerEntries(row.payContextBlockers).filter(
          (item) => nestedCodeOf(item) === 'PAY_ROLE_CARE_MINUTES_CATEGORY_MISSING',
        ),
      ),
    );
    if (!secondPersonReviewEnforced) {
      for (const advisory of advisoryPayContextMessages(row.payContextBlockers)) {
        manualReviewFlags.push(advisory);
      }
    }
    const payContextNeedsResolution =
      statusNeedsResolution(row.payContextStatus, new Set(['RESOLVED'])) ||
      providedStatusNeedsResolution(row, 'payContextStatus', new Set(['RESOLVED']));
    if (payContextNeedsResolution) {
      blockers.push(
        `Pay context is ${normaliseStatus(row.payContextStatus) || 'NOT_RESOLVED'}; resolve pay-role/export context before payroll export.`,
      );
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code: ISSUE.PAY_CONTEXT_NOT_RESOLVED,
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: { payContextStatus: normaliseStatus(row.payContextStatus) || 'NOT_RESOLVED' },
      });
    }
    for (const blocker of payContextBlockers.slice(0, 3)) {
      blockers.push(`Pay context blocker: ${blocker}`);
    }
    for (const nested of payContextNested) {
      const code = nested.code ?? ISSUE.PAY_CONTEXT_NESTED;
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code,
        rawSeverity: nestedPayContextSeverity(nested.code, nested.severity),
        resourceId,
        staffId,
        parameters: nested.code
          ? { message: nested.message }
          : { message: nested.message, nested: true },
      });
    }

    // ── Break treatment: blockers keep 3-item cap; issues full set ──
    const breakEvidence =
      row.sourceEvidence && typeof row.sourceEvidence === 'object'
        ? (row.sourceEvidence as Record<string, unknown>)
        : {};
    const breakSnapshot =
      row.breakTreatmentSnapshot && typeof row.breakTreatmentSnapshot === 'object'
        ? (row.breakTreatmentSnapshot as Record<string, unknown>)
        : {};
    const breakExplicit =
      breakEvidence.break_source === 'WORKER' ||
      ['ENTERED', 'CONFIRMED_EXPLICIT_ZERO'].includes(String(breakEvidence.breakEntryProvenance));
    const rawBreakSpan =
      row.actualStart && row.actualEnd
        ? Math.round((Date.parse(row.actualEnd) - Date.parse(row.actualStart)) / 60_000)
        : NaN;
    const currentRuleProof = row.currentBreakRuleProof;
    const breakV13 = isV13BreakSemantics(row.breakSemantics);
    const legacyNoBreakRequired =
      breakSnapshot.actualBreakMinutesProvenance === 'NONE' &&
      breakSnapshot.noBreakRequiredRuleBlockId == null &&
      row.recordedBreakMinutes === 0 &&
      currentRuleProof?.approvedTakenFactAbsent === true &&
      currentRuleProof.payRuleVersionId === (row.payContextSnapshot?.payRuleVersionId ?? null) &&
      Number.isFinite(rawBreakSpan) &&
      rawBreakSpan > 0 &&
      resolveBreakTreatment({
        breakSemantics: row.breakSemantics,
        sourceKind: 'TIMESHEET',
        employeeCategory: currentRuleProof.employeeCategory,
        rawSpanMinutes: rawBreakSpan,
        scheduledBreakMinutes: 0,
        actualBreakMinutesProvenance: 'UNRESOLVED',
        ruleBlocks: currentRuleProof.ruleBlocks,
        finalizationMode: 'FINAL',
      }).actualBreakMinutesProvenance === 'RULE_NO_BREAK_REQUIRED';
    const noBreakRequired =
      row.breakTreatmentStatus === 'NOT_APPLICABLE' &&
      breakSnapshot.status === 'NOT_APPLICABLE' &&
      breakSnapshot.sourceKind === 'TIMESHEET' &&
      breakSnapshot.treatmentKind === 'NO_BREAK' &&
      // LEGACY_28D1 (28d1 :874) accepts only a rule-less snapshot here; V13 also
      // accepts the below-trigger rule that proved no break was required.
      (breakSnapshot.ruleBlockId === null ||
        (breakV13 &&
          (breakSnapshot.ruleBlockId === breakSnapshot.noBreakRequiredRuleBlockId ||
            legacyNoBreakRequired))) &&
      ((typeof breakSnapshot.noBreakRequiredRuleBlockId === 'string' &&
        breakSnapshot.noBreakRequiredRuleBlockId.length > 0 &&
        breakSnapshot.actualBreakMinutesProvenance === 'RULE_NO_BREAK_REQUIRED') ||
        legacyNoBreakRequired) &&
      breakSnapshot.actualBreakMinutes === 0 &&
      typeof breakSnapshot.rawSpanMinutes === 'number' &&
      breakSnapshot.rawSpanMinutes > 0 &&
      Array.isArray(breakSnapshot.blockers) &&
      breakSnapshot.blockers.length === 0 &&
      Boolean(row.actualStart && row.actualEnd) &&
      rawBreakSpan === breakSnapshot.rawSpanMinutes;
    const noRuleRecordedZero = row.noTenantBreakRule === true && row.recordedBreakMinutes === 0;
    const breakDurationUnresolved =
      !salaryFlat &&
      !noRuleRecordedZero &&
      !breakExplicit &&
      (breakSnapshot.breakMinutesStatus === 'UNRESOLVED' ||
        breakEvidence.break_source === 'UNRESOLVED' ||
        breakEvidence.break_minutes_authority === 'UNRESOLVED') &&
      !(
        ['RESOLVED', 'NOT_APPLICABLE'].includes(String(row.breakTreatmentStatus)) &&
        (Number(breakSnapshot.actualBreakMinutes) > 0 ||
          // V13 only: a resolved paid meal has no unknown deduction. LEGACY_28D1
          // keeps the 28d1 check unchanged (R71, 28 Oct 2026).
          (breakV13 &&
            (breakSnapshot.treatmentKind === 'PAID_COUNTS_AS_TIME_WORKED' ||
              breakSnapshot.treatmentKind === 'PAID_NOT_TIME_WORKED') &&
            Number(breakSnapshot.payrollDeductibleBreakMinutes ?? 0) === 0) ||
          (['APPROVED_FACT_NOT_TAKEN', 'APPROVED_FACT_DURATION'].includes(
            String(breakSnapshot.actualBreakMinutesProvenance),
          ) &&
            Number(breakSnapshot.actualBreakMinutes) === 0) ||
          noBreakRequired) &&
        breakSnapshot.breakMinutesStatus !== 'UNRESOLVED'
      );
    if (breakDurationUnresolved) {
      const message =
        'Break length is unknown. Use Correct actual time to enter the break minutes or confirm that no meal break was taken.';
      // V13 (rebuild ON): a pay hold. Money is held at snapshot/export; the
      // coordinator gate and the red coordinator row do not carry it.
      (breakV13 ? exportOnlyBlockers : blockers).push(
        'Break length is unknown. Use Correct actual time to enter the break minutes or confirm that no meal break was taken.',
      );
      pushIssue(issues, {
        domain: 'BREAK',
        code: 'BREAK_DURATION_UNRESOLVED',
        rawSeverity: 'HARD',
        resourceId,
        staffId,
        parameters: breakV13
          ? { message, [ISSUE_PAY_HOLD_OWNER_PARAMETER]: 'payroll_pay' }
          : { message },
      });
    }
    const breakTreatmentBlockers = jsonBlockers(row.breakTreatmentBlockers);
    const breakNested = parseNestedBlockerItems(row.breakTreatmentBlockers);
    const breakNeedsResolution =
      !noRuleRecordedZero &&
      (statusNeedsResolution(row.breakTreatmentStatus, new Set(['RESOLVED', 'NOT_APPLICABLE'])) ||
        providedStatusNeedsResolution(
          row,
          'breakTreatmentStatus',
          new Set(['RESOLVED', 'NOT_APPLICABLE']),
        ) ||
        breakTreatmentBlockers.length > 0);
    /**
     * Break treatment decides whether an unpaid break is deducted from PAID
     * HOURS. A SALARY_FLAT worker is not paid from hours at all, so it has
     * nothing to change and the break engine never writes them a snapshot —
     * the row sits at NOT_RESOLVED permanently with nothing a coordinator can
     * do. Medea Park's salaried staff (Carmel Malcolm, Justine Barwick) were
     * held out of the 24 Aug-6 Sep run this way on 2026-09-07.
     *
     * Same rule as allowance pricing: a pay-side concern must not gate the
     * timesheet stage. It stays visible as a review flag.
     */
    if (breakNeedsResolution && salaryFlat) {
      manualReviewFlags.push(
        'Break treatment is unresolved; it does not affect a salaried worker’s pay.',
      );
    } else if (breakNeedsResolution) {
      const breakStatus = normaliseStatus(row.breakTreatmentStatus);
      blockers.push(
        `Break treatment is ${breakStatus || 'NOT_RESOLVED'}; resolve break treatment before payroll export.`,
      );
      const { code, rawSeverity } = breakStatusIssueCode(breakStatus);
      pushIssue(issues, {
        domain: 'BREAK',
        code,
        rawSeverity,
        resourceId,
        staffId,
        parameters: {
          breakTreatmentStatus: breakStatus || 'NOT_RESOLVED',
          ...breakTreatmentFactParameters(row),
        },
      });
    }
    for (const blocker of salaryFlat || noRuleRecordedZero
      ? []
      : breakTreatmentBlockers.slice(0, 3)) {
      blockers.push(`Break treatment blocker: ${blocker}`);
    }
    for (const nested of salaryFlat || noRuleRecordedZero ? [] : breakNested) {
      const code = nested.code ?? ISSUE.BREAK_NESTED;
      pushIssue(issues, {
        domain: 'BREAK',
        code,
        rawSeverity: nested.severity ?? 'HARD',
        resourceId,
        staffId,
        parameters: nested.code
          ? {
              message: nested.message,
              ...breakTreatmentFactParameters(row),
              ...(breakV13 && code === 'BREAK_DURATION_UNRESOLVED'
                ? { [ISSUE_PAY_HOLD_OWNER_PARAMETER]: 'payroll_pay' }
                : {}),
            }
          : {
              message: nested.message,
              nested: true,
              ...breakTreatmentFactParameters(row),
            },
      });
    }

    // ── Pay event facts: blockers keep 3-item cap; issues full set ──
    const payEventFactSetBlockers = jsonBlockers(row.payEventFactSetBlockers);
    const factNested = parseNestedBlockerItems(row.payEventFactSetBlockers);
    const factNeedsResolution =
      statusNeedsResolution(row.payEventFactSetStatus, new Set(['APPROVED', 'LOCKED'])) ||
      providedStatusNeedsResolution(
        row,
        'payEventFactSetStatus',
        new Set(['APPROVED', 'LOCKED']),
      ) ||
      payEventFactSetBlockers.length > 0;
    if (factNeedsResolution) {
      blockers.push(
        `Pay event facts are ${normaliseStatus(row.payEventFactSetStatus) || 'MISSING'}; approve required manual pay facts before payroll export.`,
      );
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code: ISSUE.PAY_EVENT_FACTS_NOT_READY,
        rawSeverity: 'SOFT',
        resourceId,
        staffId,
        parameters: {
          payEventFactSetStatus: normaliseStatus(row.payEventFactSetStatus) || 'MISSING',
        },
      });
    }
    for (const blocker of payEventFactSetBlockers.slice(0, 3)) {
      blockers.push(`Pay event fact blocker: ${blocker}`);
    }
    for (const nested of factNested) {
      const code = nested.code ?? ISSUE.PAY_FACT_NESTED;
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code,
        rawSeverity: nestedPayContextSeverity(nested.code, nested.severity ?? 'SOFT'),
        resourceId,
        staffId,
        parameters: nested.code
          ? { message: nested.message }
          : { message: nested.message, nested: true },
      });
    }

    if (row.providerManualAllowancePricingPending === true) {
      // Recording factual allowance evidence is the coordinator's whole job.
      // Pricing it is payroll's, and blocks neither approval NOR export as of
      // 20271975000000: withholding export withheld the shift's wages, not just
      // the allowance. The issue below stays visible so payroll can see what is
      // waiting; it simply no longer stops the worker being paid for the day.
      // The allowance itself still cannot reach a payslip — a PENDING_PRICING
      // intent is not a line in `allowances_breakdown`, which is what the pay
      // run snapshots and the export prints.
      pushIssue(issues, {
        domain: 'PAY_CONTEXT',
        code: ISSUE.PROVIDER_MANUAL_ALLOWANCE_PRICING_PENDING,
        rawSeverity: 'SOFT',
        resourceId,
        staffId,
      });
    }

    if (allowances > 0) {
      manualReviewFlags.push('Allowance component included in this timesheet.');
    }
    if (row.isOvertime) {
      manualReviewFlags.push('Contains overtime component.');
    }
    if (row.isPublicHoliday) {
      manualReviewFlags.push('Public holiday rate applied.');
    }
    if (employmentType === 'FULL_TIME' && totalHours > 8 && ordinaryHoursLimit >= 10) {
      manualReviewFlags.push('10-hour full-time ordinary-hours arrangement applied.');
    }

    return {
      id: row.id,
      staffName: row.staffName || 'Unknown staff member',
      status,
      blockerCount: blockers.length,
      blockers,
      issues,
      manualReviewFlags,
      exportOnlyBlockers,
      canExport: blockers.length === 0 && exportOnlyBlockers.length === 0 && status === 'APPROVED',
      /**
       * True when the ONLY thing standing in this row's way is that its pay
       * context has not been resolved yet, and it has not been approved.
       *
       * For a provider whose timesheets are presumed from the roster, pay
       * context resolves AT approval — so this state is simply "waiting", not
       * "something is wrong". Counting it as blocked is what put 66 of 81
       * pending rows behind a red "blocked" label with nothing a coordinator
       * could actually do, which is the complaint that started this work.
       *
       * This does NOT relax the export gate: `canExport` above still requires
       * zero blockers, and PAY_CONTEXT_NOT_RESOLVED remains a HARD issue.
       */
      /**
       * Nothing here is a coordinator's to fix.
       *
       * Derived from the issue OWNER model rather than by matching blocker
       * text: the owner registry already encodes who resolves what, and a
       * string match would silently misclassify the moment a message is
       * reworded or a new blocker is added. Measured on the live tenant
       * 2026-09-01, 66 of 81 pending rows were shown as blocked while every
       * issue on them belonged to payroll or resolved itself at approval —
       * a red count a coordinator could do nothing about, which is the
       * complaint this workstream began with.
       *
       * The export gate is untouched: `canExport` still demands zero blockers.
       */
      awaitingResolutionOnly:
        (status === 'PENDING' || status === 'SUBMITTED') &&
        // No `blockers.length > 0` guard: an unapproved row always carries the
        // not-yet-approved blocker, so that condition is unreachable here. A
        // guard no test can falsify is worse than no guard — it reads as
        // protection that was never exercised.
        !issues.some(
          (issue) =>
            // NOT_APPROVED is owned by coordinator_time, correctly — approving
            // IS their job. But it is the queue's whole purpose, not an
            // obstacle standing before it, so it must not make the row read as
            // blocked. Anything else they own genuinely does.
            issue.code !== ISSUE.NOT_APPROVED && issueResolutionOwner(issue) === 'coordinator_time',
        ),
    };
  });

  return {
    totalRows: rows.length,
    exportableRowCount: summaries.filter((row) => row.canExport).length,
    blockedRowCount: summaries.filter((row) => row.blockerCount > 0).length,
    /**
     * Rows a coordinator can actually act on: blocked for a reason other than
     * "not approved yet". Use this for anything shown to a coordinator;
     * `blockedRowCount` keeps its export meaning for payroll surfaces.
     */
    actionableBlockedRowCount: summaries.filter(
      (row) => row.blockerCount > 0 && !row.awaitingResolutionOnly,
    ).length,
    awaitingResolutionRowCount: summaries.filter((row) => row.awaitingResolutionOnly).length,
    financialHoldRowCount: summaries.filter((row) =>
      row.issues.some((issue) => issue.code === ISSUE.PROVIDER_MANUAL_ALLOWANCE_PRICING_PENDING),
    ).length,
    pendingApprovalCount: summaries.filter(
      (row) => row.status === 'PENDING' || row.status === 'SUBMITTED',
    ).length,
    rejectedCount: summaries.filter((row) => row.status === 'REJECTED').length,
    openDisputeCount: rows.reduce((sum, row) => sum + Number(row.openDisputeCount || 0), 0),
    missingClassificationCount: rows.filter(
      (row) =>
        !isSalaryFlatRow(row) && !String(row.classificationLabel || row.schadsLevel || '').trim(),
    ).length,
    missingPayCount: rows.filter(
      (row) =>
        !isSalaryFlatRow(row) &&
        (asNumber(row.totalHours) ?? 0) > 0 &&
        (asNumber(row.totalPay) ?? 0) <= 0,
    ).length,
    allowanceReviewCount: rows.filter((row) => (asNumber(row.allowances) ?? 0) > 0).length,
    overtimeReviewCount: rows.filter((row) => Boolean(row.isOvertime)).length,
    publicHolidayReviewCount: rows.filter((row) => Boolean(row.isPublicHoliday)).length,
    fullTimeAgreementGapCount: rows.filter((row) => {
      const employmentType = String(row.employmentType || '').toUpperCase();
      const totalHours = asNumber(row.totalHours) ?? 0;
      const ordinaryHoursLimit = getOrdinaryHoursLimit(row);
      return employmentType === 'FULL_TIME' && totalHours > 8 && ordinaryHoursLimit < 10;
    }).length,
    rows: summaries,
  };
}
